import React, { useState } from 'react';

import { OTHER_PREFIX } from '@personal-trainer-app/shared/src/utils/questionnaire/questionnaire-form';

import type { ChipMultiSelectProps } from './ChipMultiSelect.types';

import { Chip } from '@/components/ui/Chip';

/**
 * A custom entry is a selected chip that can be taken back off, so it borrows
 * the selected treatment rather than being a `Chip` — a `Chip` is a toggle, and
 * this one only ever goes one way.
 */
const CUSTOM_CHIP =
  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-label-sm font-medium border-stroke-accent bg-sakura-wash text-primary-300';

/**
 * Multi-select chip group with an optional "+ Other" free-text affordance.
 *
 * Selected enum chips and any custom "other" entries are all held in `value`;
 * custom entries (not present in `options`) render as removable chips. The
 * "+ Other" control uses the design-system dashed add-CTA (dashed sakura over
 * sakura wash) and reveals an inline input that commits on Enter or blur.
 */
export function ChipMultiSelect({
  options,
  value,
  onChange,
  allowOther = false,
  otherLabel = '+ Other',
  otherPlaceholder = 'Add your own…',
  ariaLabel,
}: ChipMultiSelectProps): React.JSX.Element {
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState('');

  const selected = new Set(value);
  const optionValues = new Set(options.map((option) => option.value));
  // Custom free-text entries (`other:<text>`) — anything not a known enum value.
  const customValues = value.filter((v) => !optionValues.has(v));
  // The literal `other` enum is offered via the "+ Other" affordance, not as a
  // selectable chip, so a bare `other` is never submitted.
  const visibleOptions = allowOther
    ? options.filter((option) => option.value !== 'other')
    : options;

  const toggle = (v: string): void => {
    if (selected.has(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };

  const commitOther = (): void => {
    const trimmed = otherText.trim();
    const entry = `${OTHER_PREFIX}${trimmed}`;
    if (trimmed && !value.includes(entry)) onChange([...value, entry]);
    setOtherText('');
    setOtherOpen(false);
  };

  return (
    <div role="group" aria-label={ariaLabel} className="flex flex-wrap gap-2">
      {visibleOptions.map((option) => {
        const isSelected = selected.has(option.value);
        return (
          <Chip
            key={option.value}
            label={option.label}
            selected={isSelected}
            onClick={() => toggle(option.value)}
            variant="filled"
            size="lg"
          />
        );
      })}

      {customValues.map((v) => {
        const label = v.startsWith(OTHER_PREFIX)
          ? v.slice(OTHER_PREFIX.length)
          : v;
        return (
          <span key={v} className={CUSTOM_CHIP}>
            {label}
            <button
              type="button"
              aria-label={`Remove ${label}`}
              onClick={() => onChange(value.filter((x) => x !== v))}
              className="rounded-full px-0.5 hover:bg-sakura-tint focus:outline-none focus-visible:ring-state-focus"
            >
              <span aria-hidden="true">×</span>
            </button>
          </span>
        );
      })}

      {allowOther &&
        (otherOpen ? (
          <span className="inline-flex items-center rounded-full border border-dashed border-primary-400 bg-sakura-wash px-3 py-1.5">
            <input
              autoFocus
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitOther();
                } else if (e.key === 'Escape') {
                  setOtherText('');
                  setOtherOpen(false);
                }
              }}
              onBlur={commitOther}
              placeholder={otherPlaceholder}
              aria-label={otherPlaceholder}
              className="w-32 bg-transparent text-body-lg md:text-body-sm text-neutral-50 placeholder:text-neutral-500 focus:outline-none"
            />
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setOtherOpen(true)}
            className="inline-flex items-center rounded-full border border-dashed border-primary-400 bg-sakura-wash px-3 py-1.5 text-body-sm text-primary-300 transition-colors hover:bg-sakura-tint focus:outline-none focus-visible:ring-state-focus"
          >
            {otherLabel}
          </button>
        ))}
    </div>
  );
}

export default ChipMultiSelect;
